/**
 * 画像アップロード管理要素
 * @param {String}} imageName react-hook-form 管理用キー名 画像
 * @param {String}} mineName react-hook-form 管理用キー名 拡張子
 */

/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { memo, useEffect, useRef, useState } from "react";

// CSS読み込み
import {
  mtgBody, mtgCaption, validationErrorText,
} from "assets/css/stylesCss";

import {
  none,
  uploadImgButton,
  uploadImg,
  uploadText,
} from "assets/css/shchMtmr";

import { useFormContext } from "react-hook-form";

import LabelWithHint from "components/ui/label/LabelWithHint";
import LabelTitleWithHintImg from "components/ui/label/LabelTitleWithHintImg";

import ImagePreview from "components/parts/image/imageUpload/ImagePreview";

import { getBase64 } from "utilities/getBase64";

import ModalHowToShootKyutoki from "screens/modal/ModalHowToShootKyutoki";
import ModalHowToShootGasStove from "screens/modal/ModalHowToShootGasStove";
import { isEmpty } from "utilities/string";

// 解像度別に画像を用意
const fileUploadSmall = require("assets/img/fileUpload/ico-FileUpload.png");
const fileUploadMedium = require("assets/img/fileUpload/ico-FileUpload@2x.png");
const fileUploadLarge = require("assets/img/fileUpload/ico-FileUpload@3x.png");

// コンポーネントによるレイアウト指定（cssはファイル末尾で管理）
const imageUpload = memo(
  ({ imageName1, mineName1, imageName2, mineName2, imageName3, mineName3 }) => {

    // formコンテキストからreact-hook-form操作関連メソッドを取得
    const { setValue, getValues } = useFormContext();

    // 参照を定義
    const inputRef = useRef(null);

    // 状態定義
    const [srcs, setSrcs] = useState([]);
    // メッセージ表示
    const [msgImg, setMsgImg] = useState(null);

    // 画像が添付されているか
    const [isShow, setisShow] = useState(false);

    // 初回ロード時
    useEffect(() => {

      // form管理値を取得
      const img1 = getValues(imageName1);
      const img2 = getValues(imageName2);
      const img3 = getValues(imageName3);

      // stateに設定
      if (img3) setSrcs([img1, img2, img3]);
      else if (img2) setSrcs([img1, img2]);
      else if (img1) setSrcs([img1]);

      // 画像が添付されているか確認
      setisShow(() => getArrSelectNm(getValues(imageName1), getValues(imageName2), getValues(imageName3)));

    }, [getValues, imageName1, imageName2, imageName3]);

    // 画像登録状況に変化があった場合,form管理値に保存
    // jpeg変換を行い送信するため、拡張子は[jpeg]で固定
    useEffect(() => {

      if (srcs[0]) {
        setValue(imageName1, srcs[0]);
        setValue(mineName1, "jpeg");
      } else {
        setValue(imageName1, "");
        setValue(mineName1, "");
      }
      if (srcs[1]) {
        setValue(imageName2, srcs[1]);
        setValue(mineName2, "jpeg");
      } else {
        setValue(imageName2, "");
        setValue(mineName2, "");
      }
      if (srcs[2]) {
        setValue(imageName3, srcs[2]);
        setValue(mineName3, "jpeg");
      } else {
        setValue(imageName3, "");
        setValue(mineName3, "");
      }

      // 画像が添付されているか確認
      setisShow(() => getArrSelectNm(getValues(imageName1), getValues(imageName2), getValues(imageName3)));
    }, [srcs]);

    // 登録された画像をstateに保存
    const saveFilesToState = async (evt) => {

      if (evt.currentTarget?.files && evt.currentTarget.files[0]) {
        setMsgImg(); // メッセージ初期化
        const base64 = await getBase64(evt.currentTarget.files[0], msg2mbCheck);
        if (!srcs) {
          setSrcs(base64);
        } else if (srcs.length < 3) {
          setSrcs(srcs.concat(base64));
        } else {
          setMsgImg("画像は3枚以内でご登録ください。");
        }
        inputRef.current.value = "";
      }
      setisShow(() => getArrSelectNm(getValues(imageName1), getValues(imageName2), getValues(imageName3)));

    };

    // メッセージ「2MB以下の画像を選択してください。」
    const msg2mbCheck = () => {
      setMsgImg("2MB以下の画像を選択してください。")
    }

    // 画像上のxボタンが押された時のコールバック関数。画像をstateから削除する
    const deleteImage = (n) => {

      const srcsCopy = srcs.concat();
      srcsCopy.splice(n, 1);
      setSrcs(srcsCopy);
      setMsgImg() // メッセージ削除
    };

    return (
      <>
        {/** タイトル */}
        <LabelTitleWithHintImg title="写真のアップロード" isShow={isShow} required={false}>
        </LabelTitleWithHintImg>
        {/** 他のキャプションとmarginBottom値が違うため、直書き */}
        <p css={isShow ? mtgCaption : none} style={{ marginBottom: '12px' }}>
          現在の機器の設置写真（本体周辺・配管・本体・リモコンなど）をお送りいただけると、スムーズな対応が可能です。
        </p>
        <button
          css={uploadImgButton}
          type="button"
          onClick={() => inputRef.current.click()}
        >
          <img
            css={uploadImg}
            src={fileUploadSmall}
            srcSet={`${fileUploadSmall} 1x, ${fileUploadMedium} 2x, ${fileUploadLarge} 3x`}
            alt="ファイル選択"
          />
          <span css={uploadText}>ファイルを選択</span>
        </button>
        <input
          hidden
          ref={inputRef}
          type="file"
          accept="image/*,image/heic"
          onChange={saveFilesToState}
        />
        {/** 他のキャプションとmarginBottom値が違うため、直書き */}
        <p css={mtgCaption} style={{ marginBottom: '8px' }}>
          最大3枚、1枚2MB以下、合計6MB以下までアップロードできます。
        </p>
        {/** エラーメッセージ */}
        {
          msgImg !== undefined && msgImg !== null && <p css={validationErrorText}>
            {msgImg}
          </p>
        }
        <ImagePreview
          src1={srcs.length >= 1 ? srcs[0] : null}
          src2={srcs.length >= 2 ? srcs[1] : null}
          src3={srcs.length >= 3 ? srcs[2] : null}
          onDelete={deleteImage}
        />

        {/** ヒント（給湯器の撮り方） */}
        <LabelWithHint title={"給湯器の撮り方"} isShow={isShow}>
          <ModalHowToShootKyutoki />
        </LabelWithHint>

        {/** ヒント（ガスコンロの撮り方） */}
        <LabelWithHint title={"ガスコンロの撮り方"} isShow={isShow}>
          <ModalHowToShootGasStove />
        </LabelWithHint>
      </>
    );
  }
);

/**
 * 選択されている名称を取得し返却
 * @returns resultArr 選択されている名称配列
 */
const getArrSelectNm = (img1, img2, img3) => {
  var result = true;
  if (!isEmpty(img1) || !isEmpty(img2) || !isEmpty(img3)) result = false;
  return result;
}

function base64ToBlob(base64) {
  var base64Data = base64.split(',')[1], // Data URLからBase64のデータ部分のみを取得
    data = window.atob(base64Data), // base64形式の文字列をデコード
    buff = new ArrayBuffer(data.length),
    arr = new Uint8Array(buff),
    blob,
    i,
    dataLen;
  // blobの生成
  for (i = 0, dataLen = data.length; i < dataLen; i++) {
    arr[i] = data.charCodeAt(i);
  }
  blob = new Blob([arr], { type: 'image/jpeg' });
  return blob;
}

export default imageUpload;
